import * as React from 'react'
import {ReactElement} from 'react'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledPageTitle} from '../../../components/reservations/components/CommonStyle'
import {Box} from '@mui/material'
import {AUTHENTICATION_CODE_TYPE, DO_OR_NOT} from '../../../CodeMaster'
import {NatTwoFactorAuthRepo} from './repository/NatTwoFactorAuthRepo'
import {GuestAuthBackButton, GuestAuthButton, ItemHeading, WideWhiteArea} from './common/NatTwoFactorAuthCommon'
import {FullWidthColumnFlexContainer} from '../../../styledComponents/FlexComponents'
import {PPTextFieldForResident} from '../../../components/shared/forms/resident/PPTextFieldForResident'
import {BlockSpacerSpOnly, GridContainer, GridItem} from '../../../styledComponents/ResponsiveConvenience'
import {DisplayItemInputTitleNoMargin} from '../../../styledComponents/DisplayItem'
import {
    GuestLoginInfoSessionStorageKey,
    NatGuestLoginInfoSessionStorageHelper,
} from '../../common/authentication/NatGuestLoginInfoSessionStorageHelper'
import {NatBaseResponseInterface} from '../common/repository/NatBaseResponseInterface'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {Location} from 'react-router-dom'

interface Props {
    authType: AUTHENTICATION_CODE_TYPE
    twoFactorAuthRepo: NatTwoFactorAuthRepo
    location: Location
}

interface States {
    recipient: string
    authCode: string
    authButtonDisabled: boolean
    isLoadCompleted: boolean
}

const AUTH_CODE_LENGTH_SMS_IVR = 4
const AUTH_CODE_LENGTH_EMAIL = 8

class NatGuestTwoFactorAuthInputPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, States> {
    authCodeLength: number = 0
    authCodeInputMode: string = ''

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            recipient: '',
            authCode: '',
            authButtonDisabled: true,
            isLoadCompleted: false,
        }

        switch (this.props.authType) {
            case AUTHENTICATION_CODE_TYPE.SMS:
            case AUTHENTICATION_CODE_TYPE.VOICE:
                this.authCodeLength = AUTH_CODE_LENGTH_SMS_IVR
                this.authCodeInputMode = 'numeric'
                break
            case AUTHENTICATION_CODE_TYPE.EMAIL:
                this.authCodeLength = AUTH_CODE_LENGTH_EMAIL
                this.authCodeInputMode = 'text'
                break
        }

        this.handleChangeAuthCode = this.handleChangeAuthCode.bind(this)
        this.onClickAuthButton = this.onClickAuthButton.bind(this)
        this.onClickBackButton = this.onClickBackButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter, twoFactorAuthRepo} = this.props

        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                twoFactorAuthRepo.getTwoFactorAuthRecipientsFromOwnershipTransfers.bind(twoFactorAuthRepo),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                })

            if (!response?.details) {
                return
            }

            let recipient = ''
            switch (this.props.authType) {
                case AUTHENTICATION_CODE_TYPE.SMS:
                    recipient = response?.details?.smsPhoneNumber ?? ''
                    break
                case AUTHENTICATION_CODE_TYPE.VOICE:
                    recipient = response?.details?.voicePhoneNumber ?? ''
                    break
                case AUTHENTICATION_CODE_TYPE.EMAIL:
                    recipient = response?.details?.emailAddress ?? ''
                    break
            }

            this.setState({recipient: recipient})
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    onClickBackButton(): void {
        this.props.navigate(`/guest/two-factor-auth/1/send${window.location.search}`, {replace: true})
    }

    async onClickAuthButton(): Promise<void> {
        const {authType, eventEmitter, twoFactorAuthRepo} = this.props
        const {authCode} = this.state

        await this.props.eventEmitter.showLoading()
        try {
            let response: Nullable<NatBaseResponseInterface> = null
            switch (authType) {
                case AUTHENTICATION_CODE_TYPE.SMS:
                case AUTHENTICATION_CODE_TYPE.VOICE:
                    response = await this.props.asyncCallFunction(
                        twoFactorAuthRepo.verifyAuthCodeSmsToOwnershipTransfers.bind(
                            twoFactorAuthRepo, authCode),
                        async (httpErrorOrNetworkError) => {
                            await httpErrorOrNetworkError.broadcast(eventEmitter)
                        },
                    )
                    break
                case AUTHENTICATION_CODE_TYPE.EMAIL:
                    response = await this.props.asyncCallFunction(
                        twoFactorAuthRepo.verifyAuthCodeEmailToOwnershipTransfers.bind(
                            twoFactorAuthRepo, authCode),
                        async (httpErrorOrNetworkError) => {
                            await httpErrorOrNetworkError.broadcast(eventEmitter)
                        },
                    )
                    break
            }

            if (response && response.requestError === null) {
                NatGuestLoginInfoSessionStorageHelper
                    .setSessionStorageByKey(
                        GuestLoginInfoSessionStorageKey.TWO_FACTOR_AUTH_NECESSARY,
                        DO_OR_NOT.NOT.value,
                    )

                const params = new URLSearchParams(window.location.search)
                const seniUrl = params.get('seniurl') ?? ''
                params.delete('seniurl')
                const otherParam = params.toString()

                this.props.navigate(`${seniUrl}?${otherParam}`, {
                    replace: true,
                    state: this.props.location.state,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleChangeAuthCode(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            authCode: event.target.value,
            authButtonDisabled: event.target.value.length !== this.authCodeLength,
        })
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <Box height={'15px'}/>
                <StyledPageTitle>認証コードの入力</StyledPageTitle>
                <Box height={'20px'}/>
                <StyledPageDescription>
                    {this.state.recipient}に{this.props.authType.label}を送信しました。
                    受け取った{this.authCodeLength}桁の認証コードを入力してください。
                </StyledPageDescription>
                <Box height={'20px'}/>
                <ItemHeading>
                    認証コード
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <GridContainer>
                        <GridItem over={3}>
                            <DisplayItemInputTitleNoMargin>認証コード</DisplayItemInputTitleNoMargin>
                        </GridItem>
                        <GridItem over={9}>
                            <BlockSpacerSpOnly height={'10px'}/>
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: this.authCodeLength,
                                    name: 'authCode',
                                    value: this.state.authCode,
                                    inputMode: this.authCodeInputMode,
                                }}
                                onChange={this.handleChangeAuthCode}
                                eventKeys={['authCode']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        </GridItem>
                    </GridContainer>
                    <Box height={'20px'}/>
                </WideWhiteArea>
                <Box height={'20px'}/>
                <GuestAuthButton
                    disabled={this.state.authButtonDisabled}
                    onClick={this.onClickAuthButton}
                >
                    <FullWidthColumnFlexContainer>
                        認証する
                    </FullWidthColumnFlexContainer>
                </GuestAuthButton>
                <Box height={'30px'}/>
                <GuestAuthBackButton onClick={this.onClickBackButton}>
                    認証コードの送信へ戻る
                </GuestAuthBackButton>
                <Box height={'15px'}/>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestTwoFactorAuthInputPage)

const StyledPageDescription: StyledDiv = styled.div`
  line-break: anywhere;
`
